import { useState } from 'react'
import { useParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import {
  useCreateWebhookMutation,
  useDeleteWebhookMutation,
  useListComponentsQuery,
  useListEventTypesQuery,
  useListWebhookDeliveriesQuery,
  useListWebhooksQuery,
  useRedeliverWebhookMutation,
  useRotateWebhookSecretMutation,
  useTestWebhookMutation,
  useUpdateWebhookMutation,
  type Webhook,
  type EventType,
} from '../api/authoring'
import { problemMessage } from '../api/baseApi'
import { Loading, QueryError } from '../ui/QueryState'
import { SITE_TYPE } from '../model/website'
import { ForDevelopers } from './integrations/ForDevelopers'
import { shown } from '../api/useIntent'

export function Integrations() {
  const { tenant = '' } = useParams()
  const webhooks = useListWebhooksQuery({ tenant })
  const eventTypes = useListEventTypesQuery()
  const sites = useListComponentsQuery({ tenant, type: SITE_TYPE, limit: 100 })
  const [creating, setCreating] = useState(false)
  const [secret, setSecret] = useState<string>()
  const [create, creation] = useCreateWebhookMutation()

  if (webhooks.isLoading || eventTypes.isLoading || sites.isLoading) return <Loading label="Loading integrations…" />
  if (webhooks.error) return <QueryError error={webhooks.error} title="Could not load webhooks" />
  if (eventTypes.error) return <QueryError error={eventTypes.error} title="Could not load event types" />
  if (sites.error) return <QueryError error={sites.error} title="Could not load sites" />

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="start">
        <Box>
          <Typography variant="h1">Integrations</Typography>
          <Typography color="text.secondary">
            Tell another system when something happens here, and what a website needs to show this content.
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => setCreating(true)}>Add webhook</Button>
      </Stack>
      {(webhooks.data?.webhooks ?? []).length === 0 ? (
        <Paper sx={{ p: 4 }}><Typography>No webhooks have been configured.</Typography></Paper>
      ) : (
        <Stack spacing={2}>
          {(webhooks.data?.webhooks ?? []).map((webhook) => (
            <WebhookCard key={webhook.id} tenant={tenant} webhook={webhook}
              eventType={eventTypes.data?.eventTypes.find((candidate) => candidate.type === webhook.type)}
              sites={sites.data?.components ?? []} onChanged={() => webhooks.refetch()} onSecret={setSecret} />
          ))}
        </Stack>
      )}
      <ForDevelopers tenant={tenant} />
      <CreateDialog open={creating} onClose={() => setCreating(false)} eventTypes={eventTypes.data?.eventTypes ?? []}
        sites={sites.data?.components ?? []} busy={creation.isLoading} error={creation.error}
        onCreate={async (type, url, websiteIds) => {
          const result = await create({tenant, createWebhook: {id: crypto.randomUUID(), type, destination: {url},
            conditions: websiteIds.length ? {website: websiteIds} : {}}}).unwrap()
          setCreating(false); setSecret(result.secret); await webhooks.refetch()
        }} />
      <Dialog open={Boolean(secret)} onClose={() => setSecret(undefined)} maxWidth="sm" fullWidth>
        <DialogTitle>Save this signing secret</DialogTitle>
        <DialogContent><Alert severity="warning" sx={{ mb: 2 }}>It will not be shown again.</Alert><TextField fullWidth label="Signing secret" value={secret ?? ''} slotProps={{input: {readOnly: true}}} /></DialogContent>
        <DialogActions><Button onClick={() => navigator.clipboard.writeText(secret ?? '')}>Copy</Button><Button onClick={() => setSecret(undefined)}>Done</Button></DialogActions>
      </Dialog>
    </Stack>
  )
}

function CreateDialog({open, onClose, eventTypes, sites, busy, error, onCreate}: {
  open: boolean; onClose: () => void; eventTypes: Array<{type: string; title: string; level: string}>
  sites: Array<{id: string; label?: string}>; busy: boolean; error: unknown
  onCreate: (type: string, url: string, websiteIds: string[]) => Promise<void>
}) {
  const [type, setType] = useState('')
  const [url, setUrl] = useState('')
  const [websiteIds, setWebsiteIds] = useState<string[]>([])
  const selected = eventTypes.find((eventType) => eventType.type === type)
  return <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>Add webhook</DialogTitle><DialogContent><Stack spacing={2} sx={{pt: 1}}>
	      {Boolean(error) && <Alert severity="error">{problemMessage(error, 'Could not create the webhook.')}</Alert>}
      <TextField select fullWidth label="Event" value={type} onChange={(event) => {setType(event.target.value); setWebsiteIds([])}}>
        {eventTypes.map((eventType) => <MenuItem key={eventType.type} value={eventType.type}>{eventType.title}</MenuItem>)}
      </TextField>
      <TextField label="Destination URL" value={url} onChange={(event) => setUrl(event.target.value)} placeholder="https://example.com/hooks/bluu" />
      {selected?.level === 'website' && <SitesSelect sites={sites} value={websiteIds} onChange={setWebsiteIds} />}
    </Stack></DialogContent><DialogActions><Button onClick={onClose}>Cancel</Button><Button variant="contained" disabled={busy || !type || !url} onClick={() => onCreate(type, url, websiteIds).catch(shown)}>Create</Button></DialogActions>
  </Dialog>
}

// Nothing chosen is every site, and the box says so rather than drawing
// blank, which an author cannot tell from sites that failed to load.
function SitesSelect({sites, value, onChange}: {
  sites: Array<{id: string; label?: string}>; value: string[]; onChange: (websiteIds: string[]) => void
}) {
  return <TextField select fullWidth label="Sites" value={value}
    onChange={(event) => { const chosen = event.target.value as string | string[]; onChange(typeof chosen === 'string' ? chosen.split(',') : chosen) }}
    slotProps={{select: {multiple: true, displayEmpty: true, renderValue: (values) => (values as string[]).length ? `${(values as string[]).length} selected` : 'All sites'}, inputLabel: {shrink: true}}}>
    {sites.map((site) => <MenuItem key={site.id} value={site.id}>{site.label ?? site.id}</MenuItem>)}
  </TextField>
}

function WebhookCard({tenant, webhook, eventType, sites, onChanged, onSecret}: {
  tenant: string; webhook: Webhook; eventType?: EventType; sites: Array<{id: string; label?: string}>
  onChanged: () => void; onSecret: (secret: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [update] = useUpdateWebhookMutation(); const [remove] = useDeleteWebhookMutation(); const [rotate] = useRotateWebhookSecretMutation(); const [test] = useTestWebhookMutation()
  const deliveries = useListWebhookDeliveriesQuery({tenant, webhook: webhook.id})
  const [redeliver] = useRedeliverWebhookMutation()
  const scope = webhook.conditions.website?.length ? `${webhook.conditions.website.length} site${webhook.conditions.website.length === 1 ? '' : 's'}` : 'All sites'
  return <Paper sx={{p: 2}}><Stack spacing={1.5}>
    <Stack direction="row" alignItems="center" spacing={1}><Typography variant="h3" sx={{flexGrow: 1}}>{eventType?.title ?? webhook.type}</Typography><Chip label={scope} size="small"/><FormControlLabel label="Enabled" labelPlacement="start" control={<Switch checked={webhook.enabled} onChange={async (_, enabled) => {
      if (!window.confirm(`${enabled ? 'Enable' : 'Disable'} this webhook?`)) return
      await update({tenant, webhook: webhook.id, updateWebhook: {enabled}}).unwrap(); onChanged()
    }} />} /></Stack>
    <Typography sx={{wordBreak: 'break-all'}}>{webhook.destination.url}</Typography>
    {webhook.disabledReason && <Alert severity="error">Disabled after repeated delivery failures.</Alert>}
    <Stack direction="row" spacing={1} flexWrap="wrap">
      <Button size="small" onClick={() => setEditing(true)}>Edit</Button>
      <Button size="small" onClick={() => test({tenant, webhook: webhook.id}).unwrap().then(() => deliveries.refetch()).catch(shown)}>Send test</Button>
      <Button size="small" onClick={async () => {
        if (!window.confirm('Rotate this secret? The previous secret remains valid for 24 hours.')) return
        onSecret((await rotate({tenant, webhook: webhook.id}).unwrap()).secret)
      }}>Rotate secret</Button>
      <Button size="small" color="error" onClick={async () => {
        if (!window.confirm('Delete this webhook? It will stop receiving events immediately.')) return
        await remove({tenant, webhook: webhook.id}).unwrap(); onChanged()
      }}>Delete</Button>
    </Stack>
    {deliveries.error && <Alert severity="error">{problemMessage(deliveries.error, 'Could not load deliveries.')}</Alert>}
    {(deliveries.data?.deliveries ?? []).length > 0 && <List dense disablePadding>{deliveries.data!.deliveries.slice(0, 5).map((delivery) => <ListItem key={delivery.id} disableGutters secondaryAction={<Button size="small" onClick={() => redeliver({tenant, webhook: webhook.id, delivery: delivery.id}).unwrap().then(() => deliveries.refetch()).catch(shown)}>Redeliver</Button>}><ListItemText primary={`${delivery.type} · ${delivery.status}`} secondary={`${new Date(delivery.occurredAt).toLocaleString()} · attempt ${delivery.attempt}${delivery.code ? ` · HTTP ${delivery.code}` : ''}${delivery.durationMs != null ? ` · ${delivery.durationMs} ms` : ''}${delivery.error ? ` · ${delivery.error}` : ''}`} /></ListItem>)}</List>}
    {editing && <EditWebhookDialog webhook={webhook} level={eventType?.level} sites={sites} onClose={() => setEditing(false)} onSave={async (url, websiteIds) => {
      await update({tenant, webhook: webhook.id, updateWebhook: {destination: {url}, conditions: websiteIds.length ? {website: websiteIds} : {}}}).unwrap()
      setEditing(false); onChanged()
    }} />}
  </Stack></Paper>
}

function EditWebhookDialog({webhook, level, sites, onClose, onSave}: {
  webhook: Webhook; level?: string; sites: Array<{id: string; label?: string}>; onClose: () => void
  onSave: (url: string, websiteIds: string[]) => Promise<void>
}) {
  const [url, setUrl] = useState(webhook.destination.url)
  const [websiteIds, setWebsiteIds] = useState<string[]>(webhook.conditions.website ?? [])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>()
  const save = async () => {
    setBusy(true); setError(undefined)
    try { await onSave(url, websiteIds) } catch (caught) { setError(caught); setBusy(false) }
  }
  return <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>Edit webhook</DialogTitle><DialogContent><Stack spacing={2} sx={{pt: 1}}>
      {Boolean(error) && <Alert severity="error">{problemMessage(error, 'Could not update the webhook.')}</Alert>}
      <TextField label="Event" value={webhook.type} disabled />
      <TextField label="Destination URL" value={url} onChange={(event) => setUrl(event.target.value)} />
      {level === 'website' && <SitesSelect sites={sites} value={websiteIds} onChange={setWebsiteIds} />}
    </Stack></DialogContent><DialogActions><Button onClick={onClose}>Cancel</Button><Button variant="contained" disabled={busy || !url} onClick={() => save().catch(shown)}>Save</Button></DialogActions>
  </Dialog>
}
